// Alpine controller for the area homepage-card "area-poi-picker" widget
// (Task B9).
//
// The widget lets an operator choose UP TO 2 of the area's own discover-fact
// POIs to feature on the site's homepage area card (card_poi_ids). The pool
// is the area's discover-fact POOL (id/label/thumb — NOT the property review
// pool, NOT the image pool); the selection is an ordered string[] capped at
// MAX 2.
//
// TRANSACTIONAL shell (working copy + pre-edit snapshot + ONE whole-value
// cms-image-changed on confirm) is shared with review-picker/praise-picker/
// theme-quote-picker via createTransactionalPicker(); this file supplies only
// the POI-specific pool type and available()/add()/remove() (+ byId/
// selectedPOIs) — structural clone of review-picker.ts with MAX hardcoded to
// 2 (a JS const, not a presentation knob — mirrors review-picker's own MAX/
// MIN idiom) and a leaner POI pool shape (no rating/platform/sentiment).
//
// PURE-DATA CORE: the POOL panel is click-to-add and the SELECTED panel is
// click-to-remove, so the widget is fully functional WITHOUT drag. SortableJS
// reorder (in the base) is layered ONLY on the selected list.

import { createTransactionalPicker, poolPickerMethods } from "./transactional-picker";
import { parseJsonOr } from "../shared/utils";

// One POI from the area's discover-fact pool. Keys match the handler's
// cmsAreaPOIPoolEntry json tags (internal/api/handler_cms_editor_poi.go).
interface AreaPOI {
  id: string;
  label?: string;
  thumb?: string;
}

interface CMSAreaPOIPickerConfig {
  field: string;
  // Autosave routing key — threaded into the cms-image-changed detail so the
  // cmsEditor controller routes the write to the right section overlay
  // (area:<slug>:homepage_teaser).
  target: string;
  // Raw JSON strings from the widget ctx (parsed here so the templ never has
  // to build nested Alpine literals): pool = AreaPOI[], selected = string[]
  // (the ordered card_poi_ids).
  pool: string;
  selected: string;
}

// Hardcoded selection bound — the homepage area card features at most 2 POIs
// (spec: "pick exactly 2"). Mirrors the server-side clampAreaPOIPickerEdits
// cap (internal/service/cms_area_poi_picker_clamp.go) — keep both in sync.
const MAX = 2;

export function cmsAreaPOIPicker(config: CMSAreaPOIPickerConfig) {
  const pool = parseJsonOr(config.pool, [] as AreaPOI[]);
  const selected = parseJsonOr(config.selected, [] as string[]);

  return {
    ...createTransactionalPicker<string>({
      field: config.field,
      target: config.target,
      selected,
      max: MAX,
    }),
    // available()/add()/remove(): shared click-to-add/remove POOL logic. POIs are
    // keyed by id and the search box filters the label.
    ...poolPickerMethods<AreaPOI>({
      keyOf: (p) => p.id,
      matches: (p, q) => (p.label || "").toLowerCase().includes(q),
    }),
    pool,

    // The pool record for an id (read-only display).
    byId(id: string): AreaPOI | undefined {
      return this.pool.find((p) => p.id === id);
    },

    // Ordered selected POI objects for the SELECTED panel (drops any id no
    // longer in the pool so a stale id never renders a ghost row — e.g. a
    // discover fact the operator deleted after it was featured).
    selectedPOIs(): AreaPOI[] {
      return this.selected.map((id) => this.byId(id)).filter((p): p is AreaPOI => !!p);
    },
  };
}
